import { prettyDate, share, toNumber } from "../admin/format";

/** Differences below half a paisa are rounding, not errors. */
const isOff = (value) => value !== null && value !== undefined && Math.abs(toNumber(value)) >= 0.005;

/** Percentage change from `opening` to `value`; null without a positive baseline. */
export const changeFrom = (value, opening) => {
  const base = toNumber(opening);
  return base > 0 ? ((toNumber(value) - base) / base) * 100 : null;
};

const line = (block) => ({
  name: block?.name || "",
  value: toNumber(block?.value),
  opening: toNumber(block?.opening),
});

/**
 * Shapes the /dashboard/accounts payload into what the page renders.
 * Pure and null-safe: `data` may be null while the first request is in flight.
 */
export const selectAccountsDashboard = (data, range) => {
  const kpi = data?.kpi || {};
  const profit = data?.profit || {};
  const cash = data?.cash || {};
  const position = data?.position || {};
  const suppliers = data?.suppliers || {};
  const checks = data?.checks || {};
  const lines = position.lines || {};

  const revenue = toNumber(kpi.revenue?.value);
  const grossProfit = toNumber(kpi.gross_profit?.value);
  const voucherTypes = data?.vouchers?.by_type || [];
  const lastDate = data?.bounds?.last_date || null;
  const hasActivity = toNumber(kpi.vouchers?.value) > 0;

  const issues = [];
  if (isOff(checks.balance_sheet_difference)) issues.push("balance_sheet");
  if (toNumber(checks.unbalanced_count) > 0) issues.push("unbalanced");
  if (isOff(checks.cash_difference)) issues.push("cash");

  return {
    kpi,
    trend: data?.trend || [],
    granularity: data?.range?.granularity === "day" ? "day" : "month",
    // All time has no earlier period, so there is nothing to compare with.
    allTime: Boolean(data?.range?.all_time),
    comparison: data?.range?.previous_from ? `vs ${prettyDate(data.range.previous_from)} – ${prettyDate(data.range.previous_to)}` : "",

    profit: {
      revenue,
      grossProfit,
      operatingProfit: toNumber(kpi.operating_profit?.value),
      netProfit: toNumber(kpi.net_profit?.value),
      otherIncome: toNumber(profit.non_operating_income) + toNumber(profit.unclassified),
      // Margins are meaningless on zero or negative revenue.
      grossMargin: revenue > 0 ? share(grossProfit, revenue) : null,
      netMargin: revenue > 0 ? share(toNumber(kpi.net_profit?.value), revenue) : null,
      fromOrders: profit.revenue_source === "orders",
      expenseLines: profit.expense_lines || [],
      expenseTotal: (profit.expense_lines || []).reduce((sum, row) => sum + toNumber(row.value), 0),
    },

    cash: {
      opening: toNumber(cash.opening),
      closing: toNumber(cash.closing),
      accounts: cash.accounts || [],
      activities: cash.activities || [],
      hasMovement: toNumber(kpi.cash_in?.value) + toNumber(kpi.cash_out?.value) > 0,
    },

    position: {
      asOf: position.as_of || range.to,
      openingAsOf: position.opening_as_of || null,
      assets: line(position.assets),
      liabilities: line(position.liabilities),
      equity: line(position.equity),
      receivable: line(lines.receivable),
      advances: line(lines.advances),
      inventory: line(lines.inventory),
      payable: line(lines.payable),
    },

    suppliers: {
      payableTotal: toNumber(suppliers.payable_total),
      payableCount: toNumber(suppliers.payable_count),
      advanceTotal: toNumber(suppliers.advance_total),
      advanceCount: toNumber(suppliers.advance_count),
      top: suppliers.top_payable || [],
    },

    vouchers: {
      byType: voucherTypes,
      count: voucherTypes.reduce((sum, row) => sum + toNumber(row.vouchers), 0),
      recent: data?.vouchers?.recent || [],
    },

    checks: {
      balanceSheetDifference: toNumber(checks.balance_sheet_difference),
      cashDifference: toNumber(checks.cash_difference),
      unbalancedCount: toNumber(checks.unbalanced_count),
      unbalanced: checks.unbalanced || [],
      issues,
      // Only claim the books are clean once the checks have actually run.
      clean: Boolean(data) && issues.length === 0,
    },

    lastDate,
    // No voucher posted because the window lies after the newest one on file.
    outsideData: Boolean(data) && !hasActivity && Boolean(lastDate) && Boolean(range.from) && lastDate < range.from,
  };
};
